import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  untracked,
} from '@angular/core';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { CategoryStore } from '../../../stores/category/category.state';
import { Category } from '../../../models/category';
import { BadgeModule } from 'primeng/badge';
import { DividerModule } from 'primeng/divider';
import { SharedModule } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { NgIf } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { CategoryFilterComponent } from '../category-filter/category-filter.component';
import {
  addById,
  haveSameValues,
  removeByIds,
} from '../../../utility/array.utility';

/**
 * Pick list for indicator categories backed by the `CategoryStore` selection.
 *
 * The store selection is the single source of truth, both lists are derived from it
 * (and from the current filter). Moves are applied as a diff, so selected categories
 * hidden by the filter are never lost.
 */
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-category-pick-list',
  standalone: true,
  imports: [
    BadgeModule,
    DividerModule,
    PickListModule,
    SharedModule,
    TagModule,
    NgIf,
    TooltipModule,
    CategoryFilterComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './category-pick-list.component.html',
  styleUrl: './category-pick-list.component.scss',
})
export class CategoryPickListComponent implements OnDestroy {
  /** Preselected ids; without it the current store selection is used. */
  readonly selectedIds = input<string[]>();
  @Input() keepSelection = false;
  @Input() autoselectParents = false;
  @Input() autoselectChildren = false;
  @Input() showSourceControls = false;
  /** The target list is always shown in tree order, manual reordering has no effect. */
  @Input() showTargetControls = false;
  @Output() selectionChanged = new EventEmitter<string[]>();

  readonly categoryStore = inject(CategoryStore);

  private readonly selectedIdSet = computed(
    () => new Set(this.categoryStore.selection().map(c => c._id))
  );

  /** Filtered, tree-sorted categories which are not selected yet. */
  readonly sourceList = computed(() => {
    const selected = this.selectedIdSet();
    return this.categoryStore
      .asFlatTree()
      .filter(category => !selected.has(category._id));
  });

  /** Filtered, tree-sorted selection. */
  readonly targetList = computed(() => {
    const selected = this.selectedIdSet();
    return this.categoryStore
      .asFlatTree()
      .filter(category => selected.has(category._id));
  });

  /** Number of selected categories currently hidden by the filter. */
  readonly hiddenSelectionCount = computed(() =>
    Math.max(0, this.selectedIdSet().size - this.targetList().length)
  );

  constructor() {
    // Take over `selectedIds` - also once the store has loaded its items.
    effect(
      () => {
        const ids = this.selectedIds();
        this.categoryStore.items();
        if (!ids) return;
        untracked(() => {
          if (!haveSameValues(ids, [...this.selectedIdSet()])) {
            this.categoryStore.selectMultiple(ids);
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Adds the moved categories to the selection and applies the auto-select rules.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved categories.
   */
  addToSelection(event: PickListMoveToTargetEvent): void {
    // the lists hold numbered copies - select the store items instead
    const items = this.categoryStore.getByIds(
      (event.items as Category[]).map(category => category._id)
    );
    this.categoryStore.updateSelection(
      addById(this.categoryStore.selection(), items)
    );
    this.applyAutoSelect();
  }

  /**
   * Removes the moved categories from the selection and applies the auto-select rules.
   * With `autoselectParents` a category can't be selected without its parents, so
   * removing a category also removes its descendants (otherwise it would be re-added).
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved categories.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    const items = event.items as Category[];
    const removeIds = new Set(items.map(category => category._id));
    if (this.autoselectParents) {
      items.forEach(category =>
        this.categoryStore
          .fetchCategoryChildren(category._id)
          .forEach(child => removeIds.add(child._id))
      );
    }
    this.categoryStore.updateSelection(
      removeByIds(this.categoryStore.selection(), removeIds)
    );
    this.applyAutoSelect();
  }

  /**
   * Adds parents and/or all children of the selected categories and emits the new selection.
   */
  private applyAutoSelect(): void {
    if (this.autoselectParents) this.categoryStore.addParentsToSelection();
    if (this.autoselectChildren) this.categoryStore.addChildrenToSelection();
    this.selectionChanged.emit(
      this.categoryStore.selection().map(category => category._id)
    );
  }

  /**
   * Clears the selection on destroy unless `keepSelection` is set.
   */
  ngOnDestroy(): void {
    if (this.keepSelection) return;
    this.categoryStore.clearSelection();
  }
}
